import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Table } from 'react-bootstrap';
import { FaArrowLeft, FaTrash } from 'react-icons/fa';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import PoVariantsModal from '../components/modals/PoVariantsModal';
import SupplierFormModal from '../components/modals/SupplierFormModal';
import SuccessModal from '../components/modals/SuccessModal';
import { AuthContext } from '../context/AuthContext';
import '../styles/PurchaseOrderForm.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function PurchaseOrderForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const { po_number } = useParams();
  const [searchParams] = useSearchParams();
  const { userData } = useContext(AuthContext);
  
  // Determine mode from URL path or query param
  const isEditPath = location.pathname.endsWith('/edit');
  const mode = searchParams.get('mode') || (isEditPath ? 'edit' : (po_number === 'new' ? 'create' : 'view'));
  const isEditing = mode === 'edit';
  const isViewing = mode === 'view';
  const isCreating = mode === 'create' || po_number === 'new';

  const [error, setError] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [poItems, setPoItems] = useState([]);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [supplierFormData, setSupplierFormData] = useState({
    supplier_name: '',
    email: '',
    phone: '',
    countryCode: '+94',
    address: '',
    brn: '',
    vat: '',
    bank_name: '',
    branch: '',
    account_number: '',
    payment_terms: 'Cash',
    credit_period: '',
    advance_percentage: '',
    currency_id: '',
  });
  // Style/variant selection states
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [styleVariants, setStyleVariants] = useState([]);
  const [variantQuantities, setVariantQuantities] = useState({});
  const [formData, setFormData] = useState({
    attention: '',
    supplier_id: '',
    remark: '',
    status: 'Pending',
    tolerance_limit: 0,
    delivery_date: ''
  });
  // Removed unused skuOptions state
  const [isLoading, setIsLoading] = useState(false);
  const [, setPoDetails] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdPoNumber, setCreatedPoNumber] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // Add defaultStyles state for initial loading
  const [defaultStyles, setDefaultStyles] = useState([]);

  // Fetch currencies
  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/admin/currencies/get-currencies`, {
        params: { company_code: userData?.company_code }
      });
      if (response.data.success && Array.isArray(response.data.currencies)) {
        setCurrencies(response.data.currencies);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
    }
  };

  // Fetch suppliers
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/admin/suppliers/get-suppliers`, {
          params: { company_code: userData?.company_code }
        });
        setSuppliers(response.data.suppliers || []);
      } catch (error) {
        console.error('Error fetching suppliers:', error);
        setError('Error fetching suppliers');
      }
    };

    if (userData?.company_code) {
      fetchSuppliers();
    }
  }, [userData?.company_code]);

  const refetchSuppliers = async (newSupplierId) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/admin/suppliers/get-suppliers`, {
        params: { company_code: userData?.company_code }
      });
      setSuppliers(response.data.suppliers || []);
      // Select the newly added supplier
      if (newSupplierId) {
        setFormData(prev => ({ ...prev, supplier_id: newSupplierId.toString() }));
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  // Load and refresh currencies
  useEffect(() => {
    if (userData?.company_code) {
      fetchCurrencies();
    }
  }, [userData?.company_code]);

  const handleSupplierFormSubmit = async (e) => {
    e.preventDefault();

    // Validate conditional fields
    if (supplierFormData.payment_terms === 'Credit' && !supplierFormData.credit_period) {
      alert('Credit period is required for credit payment terms');
      return;
    }

    if (supplierFormData.payment_terms === 'Advance' && !supplierFormData.advance_percentage) {
      alert('Advance percentage is required for advance payment terms');
      return;
    }

    try {
      const cleanPhone = supplierFormData.phone.replace(/\D/g, '');

      const cleanedFormData = {
        ...supplierFormData,
        country_code: supplierFormData.countryCode,
        phone: cleanPhone,
      };
      delete cleanedFormData.countryCode;

      const response = await axios.post(
        `${BASE_URL}/api/admin/suppliers/add-suppliers`,
        {
          ...cleanedFormData,
          company_code: userData?.company_code,
          created_by: userData?.id,
        }
      );

      if (response.data.success) {
        // Reset form
        setSupplierFormData({
          supplier_name: '',
          email: '',
          phone: '',
          countryCode: '+94',
          address: '',
          brn: '',
          vat: '',
          bank_name: '',
          branch: '',
          account_number: '',
          payment_terms: 'Cash',
          credit_period: '',
          advance_percentage: '',
          currency_id: '',
        });
        setShowAddSupplierModal(false);
        // Refresh suppliers and select the new one
        refetchSuppliers(response.data.supplier_id);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Error saving supplier. Please try again.');
    }
  };

  // Load data for edit/view modes
  useEffect(() => {
    const loadPOData = async () => {
      if ((isEditing || isViewing) && po_number) {
        setIsLoading(true);
        try {
          const response = await axios.get(`${BASE_URL}/api/admin/po/get-purchase-order-details/${po_number}`, {
            params: { company_code: userData?.company_code, po_type: 'direct' }
          });
          
          if (response.data.success) {
            if (isViewing) {
              // For view mode, fetch available stock for each item
              const itemsWithStock = await Promise.all(
                (response.data.items || []).map(async item => {
                  try {
                    const stockRes = await axios.get(`${BASE_URL}/api/admin/stock/get-stock-summary`, {
                      params: {
                        company_code: userData?.company_code,
                        style_number: item.style_number,
                        sku: item.sku
                      }
                    });
                    return {
                      ...item,
                      available_stock_qty: stockRes.data?.stock_qty ?? '-'
                    };
                  } catch {
                    return {
                      ...item,
                      available_stock_qty: '-'
                    };
                  }
                })
              );
              setPoDetails({ ...response.data, items: itemsWithStock });
            } else {
              setFormData({
                attention: response.data.header.attention || '',
                supplier_id: response.data.header.supplier_id?.toString() || '',
                remark: response.data.header.remark || '',
                status: response.data.header.status || 'Pending',
                tolerance_limit: response.data.header.tolerance_limit || 0,
                delivery_date: response.data.header.delivery_date ? response.data.header.delivery_date.slice(0, 10) : ''
              });
              
              const formattedItems = await Promise.all(
                (response.data.items || []).map(async item => {
                  // Always fetch available stock from backend endpoint
                  try {
                    const stockRes = await axios.get(`${BASE_URL}/api/admin/stock/get-stock-summary`, {
                      params: {
                        company_code: userData?.company_code,
                        style_number: item.style_number,
                        sku: item.sku
                      }
                    });
                    return {
                      ...item,
                      available_stock_qty: stockRes.data?.stock_qty ?? '-',
                      quantity: parseInt(item.quantity) || 0,
                      unit_price: parseFloat(item.unit_price) || 0,
                      total_price: parseFloat(item.total_price) || 0,
                      company_code: userData?.company_code
                    };
                  } catch {
                    return {
                      ...item,
                      available_stock_qty: '-',
                      quantity: parseInt(item.quantity) || 0,
                      unit_price: parseFloat(item.unit_price) || 0,
                      total_price: parseFloat(item.total_price) || 0,
                      company_code: userData?.company_code
                    };
                  }
                })
              );
              setPoItems(formattedItems);
            }
          } else {
            setError('Failed to load purchase order details');
          }
        } catch (error) {
          setError(`Error loading purchase order: ${error.message}`);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadPOData();
  }, [isEditing, isViewing, po_number, userData?.company_code]);

  // Load initial styles when component mounts
  useEffect(() => {
    const loadInitialStyles = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/admin/styles/get-styles`, {
          params: { company_code: userData?.company_code }
        });
        if (res.data.success) {
          setDefaultStyles(res.data.styles.map(style => ({
            value: style.style_number,
            label: `${style.style_number} - ${style.name}`,
            ...style
          })));
        }
      } catch (error) {
        // ignore
      }
    };
    if (userData?.company_code) {
      loadInitialStyles();
    }
  }, [userData?.company_code]);

  // Style number search
  const loadStyleOptions = async (inputValue) => {
    try {
      // If no input, return initial defaults (could be empty)
      if (!inputValue) return defaultStyles;

      const res = await axios.get(`${BASE_URL}/api/admin/styles/get-styles`, {
        params: { company_code: userData?.company_code, search: inputValue }
      });

      let styles = [];
      if (res.data && res.data.success && Array.isArray(res.data.styles)) {
        styles = res.data.styles;
      }

      // Ensure client-side filtering to prevent showing the full list if API returned everything
      const q = (inputValue || '').toString().toLowerCase();
      const filtered = styles.filter(style => {
        const sn = (style.style_number || '').toString().toLowerCase();
        const name = (style.name || '').toString().toLowerCase();
        return sn.includes(q) || name.includes(q);
      });

      return filtered.map(style => ({
        value: style.style_number,
        label: `${style.style_number} - ${style.name}`,
        ...style
      }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error in loadStyleOptions:', err);
      return [];
    }
  };

  // When a style is selected, fetch its variants and show modal
  const handleStyleSelect = async (selectedOption) => {
    setSelectedStyle(selectedOption);
    setVariantQuantities({});
    if (selectedOption) {
      try {
        const res = await axios.get(`${BASE_URL}/api/admin/styles/get-style-variants/${selectedOption.value}`, {
          params: { company_code: userData?.company_code }
        });
        if (res.data.success) {
          // Always fetch available stock for each variant from backend endpoint
          const variantsWithStock = await Promise.all(
            res.data.variants.map(async (variant) => {
              try {
                const stockRes = await axios.get(`${BASE_URL}/api/admin/stock/get-stock-summary`, {
                  params: {
                    company_code: userData?.company_code,
                    style_number: variant.style_number,
                    sku: variant.sku
                  }
                });
                return {
                  ...variant,
                  available_stock_qty: stockRes.data?.stock_qty ?? '-'
                };
              } catch {
                return {
                  ...variant,
                  available_stock_qty: '-'
                };
              }
            })
          );
          setStyleVariants(variantsWithStock);
        } else {
          setStyleVariants([]);
        }
      } catch {
        setStyleVariants([]);
      }
      setShowVariantModal(true);
    } else {
      setShowVariantModal(false);
      setStyleVariants([]);
    }
  };

  // Add validateItem function
  const validateItem = (item) => {
    if (!item.sku) return 'SKU is required';
    if (!item.quantity || item.quantity <= 0) return 'Valid quantity is required';
    if (item.unit_price < 0) return 'Unit price must be 0 or greater';
    return null;
  };

  // Helper function to show error modal
  const showError = (message) => {
    setErrorModalMessage(message);
    setShowErrorModal(true);
    setError('');
  };

  // Improved handleAddItem function
  // Add variant to PO items from modal
  const handleAddVariant = (variant) => {
    const quantity = parseInt(variantQuantities[variant.sku] || 0);
    if (!quantity || quantity <= 0) {
      showError('Please enter a valid quantity for the variant.');
      return;
    }
    const newItem = {
      sku: variant.sku,
      style_number: variant.style_number || selectedStyle?.value || '',
      style_name: selectedStyle?.name || variant.style_name || 'Unknown Product',
      color_name: variant.color_name || 'Unknown Color',
      size_name: variant.size_name || 'Unknown Size',
      fit_name: variant.fit_name || 'Unknown Fit',
      material_name: variant.material_name || 'Unknown Material',
      quantity,
      unit_price: parseFloat(variant.unit_price || 0),
      total_price: quantity * parseFloat(variant.unit_price || 0),
      company_code: userData?.company_code,
      available_stock_qty: variant.available_stock_qty
    };
    const validation = validateItem(newItem);
    if (validation) {
      showError(validation);
      return;
    }
    const existingItemIndex = poItems.findIndex(item => item.sku === newItem.sku);
    if (existingItemIndex !== -1) {
      // Update quantity and total price
      const updatedItems = [...poItems];
      updatedItems[existingItemIndex].quantity += quantity;
      updatedItems[existingItemIndex].total_price = updatedItems[existingItemIndex].quantity * updatedItems[existingItemIndex].unit_price;
      updatedItems[existingItemIndex].available_stock_qty = variant.available_stock_qty;
      setPoItems(updatedItems);
    } else {
      setPoItems([...poItems, newItem]);
    }
    setVariantQuantities({ ...variantQuantities, [variant.sku]: '' });
    setError('');
  };

  const handleRemoveItem = (sku) => {
    setPoItems(poItems.filter(item => item.sku !== sku));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const poData = {
        company_code: userData.company_code,
        po_type: 'direct',
        supplier_id: parseInt(formData.supplier_id),
        attention: formData.attention.trim(),
        remark: formData.remark ? formData.remark.trim() : '',
        status: formData.status || 'Pending',
        tolerance_limit: parseFloat(formData.tolerance_limit) || 0,
        delivery_date: formData.delivery_date || null,
        items: poItems.map(item => ({
          sku: item.sku,
          style_number: item.style_number || item.style || '',
          color_name: item.color_name || '',
          size_name: item.size_name || '',
          fit_name: item.fit_name || '',
          material_name: item.material_name || '',
          quantity: parseInt(item.quantity),
          unit_price: parseFloat(item.unit_price),
          company_code: userData.company_code
        }))
      };

      let response;
      if (isEditing) {
        response = await axios.put(`${BASE_URL}/api/admin/po/update-purchase-orders/${po_number}`, poData);
      } else {
        response = await axios.post(`${BASE_URL}/api/admin/po/create-purchase-order`, poData);
      }

      if (response.data.success) {
        if (isEditing) {
          navigate(location.state?.from || '/merchandising/po');
        } else {
          // show success modal with PO number returned from backend
          const poNumber = response.data.po_number || '';
          setCreatedPoNumber(poNumber);
          setShowSuccessModal(true);
        }
      } else {
        setError(response.data.message || 'Operation failed');
        showError(response.data.message || 'Operation failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // View mode is handled by ViewPurchaseOrder.js. This page only supports create and edit modes.
  if (isViewing) {
    return null;
  }

  return (
    <Container fluid className="create-po-container">
      <Card className="mb-3">
        <Card.Header className="d-flex create-po-title">
          <h4>
            {isCreating
              ? "Create Purchase Order"
              : isEditing
                ? `Edit Purchase Order: ${po_number}`
                : `Purchase Order Details - ${po_number}`}
          </h4>
          <Button
            variant="secondary"
            onClick={() =>
              navigate(location.state?.from || "/merchandising/po")
            }
            className="cpo-back-button"
          >
            <FaArrowLeft />
            Back to List
          </Button>
        </Card.Header>
      </Card>

      {/* Header Information Card */}
      <Card className="mb-4">
        <Card.Header>
          <h4>Header Information</h4>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError("")}>
              {error}
            </Alert>
          )}
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group controlId="formSupplier">
                  <Form.Label> Supplier </Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Select
                      value={formData.supplier_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          supplier_id: e.target.value,
                        })
                      }
                      disabled={isViewing}
                      required
                      style={{ flex: 1 }}
                    >
                      <option value="">Select a supplier</option>
                      {suppliers.map((supplier) => (
                        <option
                          key={supplier.supplier_id}
                          value={supplier.supplier_id}
                        >
                          {supplier.supplier_name}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  {!isViewing && (
                    <span
                      className="text-primary"
                      onClick={() => setShowAddSupplierModal(true)}
                      title="Add New Supplier"
                      style={{
                        padding: "0.375rem 0.75rem",
                        whiteSpace: "nowrap",
                        fontSize: "0.8rem",
                        cursor: "pointer",
                      }}
                    >
                      + New Supplier
                    </span>
                  )}
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="formAttention">
                  <Form.Label>Attention</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.attention}
                    onChange={(e) =>
                      setFormData({ ...formData, attention: e.target.value })
                    }
                    placeholder="Enter attention name"
                    disabled={isViewing}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="formDeliveryDate">
                  <Form.Label>Delivery Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.delivery_date}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        delivery_date: e.target.value,
                      })
                    }
                    disabled={isViewing}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="formToleranceLimit">
                  <Form.Label>Tolerance Limit (%)</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    max={100}
                    value={formData.tolerance_limit}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        tolerance_limit: e.target.value,
                      })
                    }
                    placeholder="Enter tolerance %"
                    disabled={isViewing}
                  />
                  <Form.Text className="text-muted">
                    Allowed range: 0 - 100
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formRemark">
                  <Form.Label>Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.remark}
                    onChange={(e) =>
                      setFormData({ ...formData, remark: e.target.value })
                    }
                    placeholder="Enter remarks"
                    disabled={isViewing}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Product Information Card */}
      <Card>
        <Card.Header>
          <h4>Product Information</h4>
        </Card.Header>
        <Card.Body>
          {/* Product Number Search Section for Create/Edit modes */}
          {(isCreating || isEditing) && (
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    Search Product Number <span className="text-danger">*</span>
                  </Form.Label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions={defaultStyles}
                    loadOptions={loadStyleOptions}
                    value={selectedStyle}
                    onChange={handleStyleSelect}
                    placeholder="Type to search"
                    isClearable
                    isSearchable
                    menuPlacement="auto"
                    styles={{
                      container: (base) => ({
                        ...base,
                        width: "320px",
                      }),
                      control: (base, state) => ({
                        ...base,
                        minHeight: "38px",
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected
                          ? "#007bff"
                          : state.isFocused
                            ? "#f8f9fa"
                            : "white",
                        color: state.isSelected ? "white" : "#212529",
                      }),
                    }}
                    noOptionsMessage={({ inputValue }) =>
                      !inputValue
                        ? "Type to search products..."
                        : inputValue.length < 2
                          ? "Type at least 2 characters..."
                          : "No products found"
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* Variant Modal as component */}
          <PoVariantsModal
            show={showVariantModal}
            onHide={() => setShowVariantModal(false)}
            selectedStyle={selectedStyle}
            styleVariants={styleVariants}
            variantQuantities={variantQuantities}
            setVariantQuantities={setVariantQuantities}
            handleAddVariant={handleAddVariant}
            handleAddVariantsBatch={(variants) => {
              // Add all variants to PO items in one go
              let updatedItems = [...poItems];
              let errorMsg = "";
              variants.forEach((variant) => {
                const quantity = parseInt(variantQuantities[variant.sku] || 0);
                if (!quantity || quantity <= 0) return;
                const newItem = {
                  sku: variant.sku,
                  style_number:
                    variant.style_number || selectedStyle?.value || "",
                  style_name:
                    selectedStyle?.name ||
                    variant.style_name ||
                    "Unknown Product",
                  color_name: variant.color_name || "Unknown Color",
                  size_name: variant.size_name || "Unknown Size",
                  fit_name: variant.fit_name || "Unknown Fit",
                  material_name: variant.material_name || "Unknown Material",
                  quantity,
                  unit_price: parseFloat(variant.unit_price || 0),
                  total_price: quantity * parseFloat(variant.unit_price || 0),
                  company_code: userData?.company_code,
                  available_stock_qty: variant.available_stock_qty
                };
                const validation = validateItem(newItem);
                if (validation) {
                  errorMsg = validation;
                  return;
                }
                const existingItemIndex = updatedItems.findIndex(
                  (item) => item.sku === newItem.sku,
                );
                if (existingItemIndex !== -1) {
                  updatedItems[existingItemIndex].quantity += quantity;
                  updatedItems[existingItemIndex].total_price =
                    updatedItems[existingItemIndex].quantity *
                    updatedItems[existingItemIndex].unit_price;
                } else {
                  updatedItems.push(newItem);
                }
              });
              // Commit batch additions
              setPoItems(updatedItems);
              setError(errorMsg);
              // Optionally clear all quantities after add
              setVariantQuantities({});
            }}
          />

          <Row className="mb-3">
            <Col>
              <h6>Items</h6>
              <div className="table-responsive">
                <Table striped bordered hover className="product-info-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>SKU</th>
                      <th>Product</th>
                      <th>Color</th>
                      <th>Size</th>
                      <th>Fit</th>
                      <th>Material</th>
                      <th>Quantity</th>
                      <th>Available Stock</th>
                      <th>Unit Price</th>
                      <th>Total Price</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {poItems.map((item, index) => (
                      <tr key={item.sku}>
                        <td>{index + 1}</td>
                        <td>{item.sku}</td>
                        <td>{item.style_name}</td>
                        <td>{item.color_name}</td>
                        <td>{item.size_name}</td>
                        <td>{item.fit_name}</td>
                        <td>{item.material_name}</td>
                        <td className="number-col">
                          {isViewing ? (
                            item.quantity
                          ) : (
                            <Form.Control
                              type="number"
                              value={item.quantity}
                              min={1}
                              onChange={(e) => {
                                const newQuantity = Math.max(
                                  1,
                                  parseInt(e.target.value) || 1,
                                );
                                const updatedItems = poItems.map((i) =>
                                  i.sku === item.sku
                                    ? {
                                        ...i,
                                        quantity: newQuantity,
                                        total_price: newQuantity * i.unit_price,
                                      }
                                    : i,
                                );
                                setPoItems(updatedItems);
                              }}
                              style={{ width: 80 }}
                            />
                          )}
                        </td>
                        <td className="number-col">
                          {item.available_stock_qty !== undefined &&
                          item.available_stock_qty !== null
                            ? item.available_stock_qty
                            : "-"}
                        </td>
                        <td className="number-col">
                          ${item.unit_price.toFixed(2)}
                        </td>
                        <td className="number-col">
                          ${item.total_price.toFixed(2)}
                        </td>
                        <td>
                          <FaTrash
                            onClick={() => handleRemoveItem(item.sku)}
                            className="po-remove-icon"
                            title="Remove Item"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="table-info">
                      <th colSpan="9"></th>
                      <th className="text-end">Total</th>
                      <th className="number-col">
                        $
                        {(
                          poItems.reduce(
                            (sum, item) =>
                              sum +
                              parseFloat(item.unit_price) *
                                parseInt(item.quantity),
                            0,
                          ) || 0
                        ).toFixed(2)}
                      </th>
                      <th></th>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Action Buttons at the bottom of the page */}
      <div className="d-flex justify-content-end mt-4">
        {!isViewing && (
          <Button
            variant="primary"
            type="submit"
            className="me-2"
            disabled={isLoading}
            onClick={handleSubmit}
          >
            {isEditing ? "Update Purchase Order" : "Create Purchase Order"}
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={() => navigate(location.state?.from || "/merchandising/po")}
          disabled={isLoading}
        >
          Cancel
        </Button>
      </div>
      <SuccessModal
        show={showSuccessModal}
        onHide={() => {
          setShowSuccessModal(false);
          // Navigate back to list after dismissing
          navigate(location.state?.from || "/merchandising/po");
        }}
        variant="success"
        modalTitle="Purchase Order Created"
        message={
          createdPoNumber ? (
            <span>
              Purchase Order <strong>{createdPoNumber}</strong> created
              successfully.
            </span>
          ) : (
            "Purchase Order created successfully."
          )
        }
      />
      <SuccessModal
        show={showErrorModal}
        onHide={() => {
          setShowErrorModal(false);
        }}
        variant="error"
        modalTitle="Operation Failed"
        message={errorModalMessage}
      />
      <SupplierFormModal
        show={showAddSupplierModal}
        onHide={() => {
          setShowAddSupplierModal(false);
          // Reset form when closing
          setSupplierFormData({
            supplier_name: "",
            email: "",
            phone: "",
            countryCode: "+94",
            address: "",
            brn: "",
            vat: "",
            bank_name: "",
            branch: "",
            account_number: "",
            payment_terms: "Cash",
            credit_period: "",
            advance_percentage: "",
            currency_id: "",
          });
        }}
        onSubmit={handleSupplierFormSubmit}
        editingId={null}
        formData={supplierFormData}
        setFormData={setSupplierFormData}
        currencies={currencies}
        onCurrencyAdded={() => {
          // Refresh currencies after adding new one
          fetchCurrencies();
        }}
      />
    </Container>
  );
}
